import React from 'react';

import TrustSignals, {TrustSignalIcon} from '../trust-signals/TrustSignals';
import ContactCard, {ContactCardIcon} from '../contact-card/ContactCard';
import RelatedLinks, {RelatedLinkIcon} from '../related-links/RelatedLinks';
import SocialLinks from '../social-links/SocialLinks';
import SidebarArticle, {
    SidebarArticleCta,
    SidebarArticleFooterCta,
    SidebarArticleIconToken,
    SidebarArticleItem,
} from '../sidebar-article/SidebarArticle';


export type SidebarBlock =
    | TrustSignalsBlock
    | ContactCardBlock
    | SidebarArticleBlock
    | RelatedLinksBlock
    | SocialLinksBlock;

export type TrustSignalsBlock = {
    type: 'trustSignals';
    title?: string;
    items: {
        icon: TrustSignalIcon;
        text: string;
    }[];
};

export type ContactCardBlock = {
    type: 'contactCard';
    title: string;
    description?: string;
    icon?: ContactCardIcon;
    variant?: 'clean' | 'soft' | 'outline';
    cta: {
        label: string;
        href: string;
    };
};

export type RelatedLinksBlock = {
    type: 'relatedLinks';
    title?: string;
    items: {
        label: string;
        href: string;
        icon?: RelatedLinkIcon;
    }[];
};

export type SidebarArticleBlock = {
    type: 'sidebarArticle';
    title?: React.ReactNode;
    titleComponent?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    description?: React.ReactNode;
    items: SidebarArticleItem[];
    headerCta?: SidebarArticleCta;
    footerCta?: SidebarArticleFooterCta;
    accentColor?: string;
    accentColour?: string;
    itemCtaIconToken?: SidebarArticleIconToken;
};

export type SocialLinksBlock = {
    type: 'socialLinks';
    title?: string;
    profiles: {
        platform: 'instagram' | 'tiktok' | 'facebook' | 'linkedin' | 'youtube';
        href: string;
        handle?: string;
        postsHref?: string;
    }[];
};


export type SidebarRendererProps = {
    blocks: SidebarBlock[];
    dark?: boolean;
    spacing?: 'tight' | 'normal' | 'loose';
};


const SPACING_MAP = {
    tight: 'space-y-3',
    normal: 'space-y-6',
    loose: 'space-y-8',
};

/* ------------------------------------------------------------------
 * Renderer
 * ---------------------------------------------------------------- */

const SidebarRenderer = ({
                             blocks,
                             dark,
                             spacing = 'normal',
                         }: SidebarRendererProps) => {
    if (!blocks?.length) return null;

    return (
        <aside className={SPACING_MAP[spacing]}>
            {blocks.map((block, index) => {
                switch (block.type) {
                    case 'trustSignals':
                        return (
                            <TrustSignals
                                key={index}
                                title={block.title}
                                items={block.items}
                                dark={dark}
                            />
                        );

                    case 'contactCard':
                        return (
                            <ContactCard
                                key={index}
                                title={block.title}
                                description={block.description}
                                icon={block.icon}
                                variant={block.variant}
                                cta={block.cta}
                                dark={dark}
                            />
                        );

                    case 'sidebarArticle':
                        return (
                            <SidebarArticle
                                key={index}
                                title={block.title}
                                titleComponent={block.titleComponent}
                                description={block.description}
                                items={block.items}
                                headerCta={block.headerCta}
                                footerCta={block.footerCta}
                                accentColor={block.accentColor}
                                accentColour={block.accentColour}
                                itemCtaIconToken={block.itemCtaIconToken}
                                dark={dark}
                            />
                        );

                    case 'relatedLinks':
                        return (
                            <RelatedLinks
                                key={index}
                                title={block.title}
                                items={block.items}
                                dark={dark}
                            />
                        );

                    case 'socialLinks':
                        return (
                            <SocialLinks
                                key={index}
                                title={block.title}
                                profiles={block.profiles}
                                dark={dark}
                            />
                        );

                    default:
                        return null;
                }
            })}
        </aside>
    );
};

export default SidebarRenderer;
